import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Sparkles } from 'lucide-react';
import BrandPreview from './BrandPreview';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/jhtdaos4_SMG%20ALL%20LOGO%20PNG.png';

export default function Hero() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      setMouse({ x, y });
    };
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const blob1 = {
    transform: `translate3d(${mouse.x * -40}px, ${mouse.y * -25 + scrollY * 0.15}px, 0)`,
  };
  const blob2 = {
    transform: `translate3d(${mouse.x * 30}px, ${mouse.y * 20 - scrollY * 0.1}px, 0)`,
  };
  const logoTilt = {
    transform: `perspective(1400px) rotateX(${(-mouse.y * 5).toFixed(2)}deg) rotateY(${(mouse.x * 8).toFixed(2)}deg)`,
    transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)',
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative pt-28 md:pt-32 overflow-hidden"
      style={{ transformStyle: 'preserve-3d' }}
    >
      <div className="hero-blob" style={blob1} />
      <div className="hero-blob-2" style={blob2} />

      {/* Top marquee */}
      <div className="relative border-y border-white/10 py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee text-[11px] md:text-xs tracking-[0.4em] text-white/60">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="mx-6 flex items-center gap-6">
              <span className="text-[#FF3D2E]">•</span> AI · SI · CO-BRAND · CULTURE · ROI
            </span>
          ))}
        </div>
      </div>

      <div className="relative px-5 md:px-10 lg:px-14 pt-10 md:pt-16">
        <div className="flex items-center gap-4 mb-8 md:mb-10 fade-up" style={{ animationDelay: '0.1s' }}>
          <span className="w-10 h-px bg-[#FF3D2E]" />
          <p className="text-[9px] md:text-[11px] tracking-[0.28em] md:tracking-[0.3em] text-white/70 leading-relaxed">
            A CREATIVE STUDIO BUILT AROUND REAL BRAND DEVELOPMENT · AI &amp; SI BRAND DEVELOPMENT · CO-BRANDING OPPORTUNITIES
          </p>
        </div>

        {/* Hero logo lockup */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Logo */}
          <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: '0.2s' }}>
            <div
              className="relative will-change-transform"
              style={logoTilt}
            >
              {/* Soft glow behind */}
              <span
                className="absolute inset-0 -m-10 md:-m-20 rounded-full blur-3xl opacity-60 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(closest-side, rgba(255,61,46,0.35), rgba(120,30,160,0.18) 50%, transparent 75%)',
                }}
              />
              <img
                src={LOGO_URL}
                alt="SMG Media Group"
                draggable={false}
                loading="eager"
                decoding="async"
                style={{ imageRendering: 'auto' }}
                className="relative w-[78%] max-w-[300px] md:max-w-[460px] mx-auto lg:mx-0 select-none"
              />
              {/* Decorative crosshair lines */}
              <span className="hidden md:block absolute -left-6 top-1/2 -translate-y-1/2 w-10 h-px bg-white/15" />
              <span className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-10 h-px bg-white/15" />
            </div>
            {/* Caption under logo */}
            <div className="mt-6 flex items-center gap-4 justify-center lg:justify-start">
              <span className="text-[10px] tracking-[0.4em] text-white/45">EST. 2023</span>
              <span className="w-8 h-px bg-white/15" />
              <span className="text-[10px] tracking-[0.4em] text-white/45">SAN DIEGO · NASHVILLE</span>
            </div>
          </div>

          {/* Description */}
          <div className="lg:col-span-6 max-w-xl fade-up" style={{ animationDelay: '0.4s' }}>
            <p className="text-[10px] tracking-[0.35em] text-[#FF3D2E] mb-5">— THE MANIFESTO</p>
            <h1 className="font-display text-[2.15rem] leading-[1.02] md:text-5xl lg:text-[3.5rem] tracking-tight text-white mb-6">
              The brands still hiring agencies for social posts have already lost.
              <span className="block text-white/60 mt-3 text-[1.55rem] md:text-4xl lg:text-5xl">
                We build category dominance — with AI, culture, and revenue as one system.
              </span>
            </h1>
            <p className="text-white/75 text-[15px] md:text-lg leading-relaxed">
              SMG Media Group is a brand-growth studio for lifestyle-driven companies, co-branded partnerships, and revenue-first campaigns — engineered with Strategic Intelligence and culture-native execution.
            </p>

            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#contact"
                className="group relative inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#FF3D2E] text-white text-[11px] tracking-[0.25em] hover:bg-[#ff5446] transition-colors overflow-hidden"
                data-testid="hero-build-btn"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  BUILD WITH SMG
                  <ArrowDown className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform" />
                </span>
                <span className="absolute inset-0 bg-black/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              </a>
              <a
                href="#services"
                className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-white/30 text-[11px] tracking-[0.25em] text-white hover:bg-white hover:text-black transition-colors"
                data-testid="hero-capabilities-btn"
              >
                <Sparkles className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform" />
                VIEW OUR CAPABILITIES
              </a>
            </div>

            {/* Credibility chip strip */}
            <div className="mt-10 flex flex-wrap gap-2" data-testid="hero-credibility-strip">
              {[
                'Brand Strategy',
                'Co-Brand',
                'AI + SI',
                'Social Systems',
                'Paid Media',
                'Experiential',
                'Revenue Forecasting',
                'Lifestyle Campaigns',
              ].map((c) => (
                <span
                  key={c}
                  className="text-[10px] tracking-[0.25em] text-white/75 border border-white/15 px-3 py-1.5 hover:border-white/40 hover:text-white transition-colors"
                >
                  {c}
                </span>
              ))}
            </div>

            {/* Mini stat band */}
            <div className="mt-10 grid grid-cols-3 gap-3 md:gap-4 border-t border-white/10 pt-6">
              <div>
                <p className="font-display text-xl md:text-2xl text-white">320+</p>
                <p className="text-[9px] tracking-[0.28em] md:tracking-[0.3em] text-white/50 mt-1">CREATORS</p>
              </div>
              <div>
                <p className="font-display text-xl md:text-2xl text-white">$340M</p>
                <p className="text-[9px] tracking-[0.28em] md:tracking-[0.3em] text-white/50 mt-1">REVENUE</p>
              </div>
              <div>
                <p className="font-display text-xl md:text-2xl text-white">4.7x</p>
                <p className="text-[9px] tracking-[0.28em] md:tracking-[0.3em] text-white/50 mt-1">ROAS</p>
              </div>
            </div>
          </div>
        </div>

        {/* Live Brand Preview — the differentiator */}
        <div className="mt-16 md:mt-20 max-w-4xl fade-up" style={{ animationDelay: '0.6s' }}>
          <BrandPreview />
        </div>

        {/* Bottom row */}
        <div
          className="mt-16 md:mt-20 flex items-center justify-between text-[11px] tracking-[0.3em] text-white/60 border-t border-white/10 pt-6 fade-up"
          style={{ animationDelay: '0.6s' }}
        >
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3D2E] animate-pulse" />
            BOOKING NOW · 2026
          </div>
          <div className="hidden md:flex items-center gap-2">
            SCROLL <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
}
